import {useTableState, useTimeout} from '../../custom-hooks';
import React, {useCallback, useEffect, useState} from 'react';
import Grid from '@material-ui/core/Grid';
import CTable from '../common/ctable';
import {showError} from '../../helpers';
import Loader from '../common/loader';
import {columns} from './eventsAndMetrics-table-def';
import EventsAndMetricsExpanded from './eventsAndMetrics-expanded';
import Button from '@material-ui/core/Button';
import EventsAndMetricsDatepicker from './eventsAndMetrics-datepicker';
import EventsAndMetricsInfoDialog from './eventsAndMetrics-infoDialog';
import EventsAndMetricsRemovedOldDialog from './eventsAndMetrics-removedOldDialog';
import AppActions from '../../actions/app-actions';
import ReactDOM from 'react-dom';

const timeoutInMilliSec = 30 * 1000;
const columnDefinition = columns();

const EventsAndMetrics = () => {
  const [active, setActive] = useState('today');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [openRemoveOldDialog, setOpenRemoveOldDialog] = useState(false);

  const [list, setList] = useState([]);
  const [subscription, setSubscription] = useState([]);

  const [pageLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [startTimer] = useTimeout(refreshData, timeoutInMilliSec);
  const { colDef, selectedRows, onFilterChange, onColumnChange } = useTableState(columnDefinition);

  const body = () => {
    if(loading)
      return loader();
    else {
      return (
        <>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              {headerButtons()}
            </Grid>
            <Grid item xs={12}>
              {table()}
              {infoDialog()}
              {removeOldDialog()}
            </Grid>
          </Grid>
        </>
      );
    }
  };

  const loader = () => (<div><Loader show={true} /></div>);

  const headerButtons = () => (
    <Grid container justify='flex-end' style={{alignItems: 'center'}}>
      <Grid item style={{paddingRight: '20px'}}>
        <Button variant='text' style={{color: 'gray'}} onClick={() => setOpenInfoDialog(true)}>
          Events & metrics integration info
        </Button>
      </Grid>
      <Grid item>
        <Button variant='contained' color='primary' onClick={() => setOpenRemoveOldDialog(true)}>
          Remove old events & metrics
        </Button>
      </Grid>
    </Grid>
  );

  function table () {
    return (
      <div className='tab-container vcam-container'>
        {
          pageLoading
            ? loader()
            : (
              <CTable
                data={list}
                colDef={colDef}
                onExpand={onExpand}
                toolbar={datepicker}
                selectedRows={selectedRows}
                onFilterChange={onFilterChange}
                onColumnChange={onColumnChange}
              />
            )
        }
      </div>
    );
  }

  const infoDialog = () => (
    <EventsAndMetricsInfoDialog data={subscription} openDialog={openInfoDialog} updateDialog={setOpenInfoDialog} />
  );

  const removeOldDialog = () => (
    <EventsAndMetricsRemovedOldDialog remove={remove} openDialog={openRemoveOldDialog} updateDialog={setOpenRemoveOldDialog} />
  );

  const datepicker = () => <EventsAndMetricsDatepicker filter={filterFromDatepicker} chooseDate={chooseDate} />

  const onExpand = useCallback(rowData => {
    return <EventsAndMetricsExpanded {...rowData} onUpdate={onUpdate} />
  }, [onUpdate]);

  const onUpdate = useCallback(data => {
    const newList = list.map(item => item.id === data.id ? {...item, ...data } : item);
    setList(newList);
  }, [list]);

  function fetchDetails(didMount) {
    if(active === 'today') {
      startDate.setDate(new Date().getDate());
      endDate.setDate(new Date().getDate());
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
    }
    filterByDate(startDate, endDate, didMount);
    AppActions.subscribeToEventsAndMetrics()
      .then(response => setSubscription(response.body || []))
      .catch(error => showError(error));
  }

  function filterByDate(startDate, endDate, didMount) {
    AppActions.getListOfNotifications()
      .then(response => {
        let data = response.body || [];
        data = data.filter(item => {
          const itemDate = new Date(item.timestamp);
          return itemDate >= startDate && itemDate <= endDate;
        });
        ReactDOM.unstable_batchedUpdates(() => {
          setList(data);
          stopLoading(didMount);
        });
      })
      .catch(error => {
        showError(error);
        stopLoading(didMount);
      })
      .finally(() => startTimer());
  }

  const filterFromDatepicker = (filterType, startDate, endDate) => {
    setActive(filterType);
    setEndDate(endDate);
    setStartDate(startDate);
    filterByDate(startDate, endDate);
  }

  const chooseDate = (startDate, endDate) => {
    setActive('');
    setEndDate(endDate);
    setStartDate(startDate);
    filterByDate(startDate, endDate);
  }

  const remove = (type, olderThan) => {
    const typeAll = 'all';
    if(type === typeAll) {
      if(olderThan === typeAll)
        removeNotifications(
          AppActions.removeNotifications(),
          `Notifications grouped older than ${olderThan} removed successfully`,
          `Can't delete notifications grouped older than ${olderThan}`
        );
      else
        removeNotificationsGrouped(
          {olderThan},
          `Notifications grouped older than ${olderThan} removed successfully`,
          `Can't delete notifications grouped older than ${olderThan}`
        );
    } else {
      if(olderThan === typeAll)
        removeNotificationsGrouped(
          {type},
          `${type} removed successfully`,
          `Can't delete ${type}`
        );
      else
        removeNotificationsGrouped(
          {olderThan, type},
          `${type} older than ${olderThan} removed successfully`,
          `Can't delete ${type} older than ${olderThan}`
        );
    }
  }

  function removeNotificationsGrouped(params, successMsg, errorMsg) {
    removeNotifications(AppActions.removeNotificationsGrouped(params), successMsg, errorMsg);
  }

  function removeNotifications(remove, successMsg, errorMsg) {
    remove
      .then(() => {
        AppActions.setSnackbar(successMsg);
        refreshData();
      })
      .catch(error => showError(error, errorMsg));
  }

  function refreshData() {
    fetchDetails();
  }

  useEffect(() => fetchDetails(true), []);

  const stopLoading = (didMount) => {
    if(didMount)
      setLoading(() => false);
  };

  return (
    <>
      {body()}
    </>
  );
}

export default EventsAndMetrics;
